/* eslint-disable @typescript-eslint/no-unused-vars */
import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import * as tus from 'tus-js-client';
import mediaService from '../services/media-service';
import { MediaObject } from '../types/media-types';
import { v4 as uuidv4 } from 'uuid';
import { useToast } from 'src/components/ui/use-toast';
import { uniqBy } from 'lodash';
import { initDB, storeFile, getFile, deleteFile } from '../../../shared/services/indexedDBService';
import { useBrandSelection } from 'src/providers/BrandSelectionProvider';
import { Logger } from 'src/utils/logger';
import { wait } from 'src/lib/utils/wait';

// define types for media upload queue
export interface UploadItem {
  id?: string;
  tempId: string; // local temporal id before get id from server
  fileName: string;
  fileSize: number;
  file: File;
  progress: number;
  status: 'pending' | 'in-progress' | 'uploaded' | 'failed';
  uploadUrl: string;
  uploadToken: string;
  path: string[];
  error?: string; // To store error message on failure
}

export interface PersistedUpload {
  id: string;
  fileName: string;
  fileSize: number;
  progress: number;
  uploadUrl: string;
  uploadToken: string;
  path: string[];
  fileType: string;
}

interface MediaUploadContextType {
  uploadItems: UploadItem[];
  persistedUploads: PersistedUpload[];
  validResumableUploads: () => PersistedUpload[];
  isUploading: boolean;
  handleUpload: (params: {
    files: File[];
    userPath: string[];
    onSuccess: (items: UploadItem[]) => void;
    onError: (error: any) => void;
    onSingleFileError: (item: UploadItem, error: any) => void;
    onSingleFileSuccess: (item: UploadItem, mediaObject: MediaObject) => void;
  }) => Promise<void>;
  stopUpload: (id: string) => Promise<void>;
  uploadCanBeCancelled: (id: string) => boolean;
  removePersistedUpload: (id: string) => Promise<void>;
  currentUploads: Record<string, tus.Upload>;
  resumeUpload: (params: {
    persistedUpload: PersistedUpload;
    onSuccess: (item: UploadItem, mediaObject: MediaObject) => void;
    onError: (error: any) => void;
  }) => Promise<void>;
  resumeUploadWithFile: (params: {
    file: File;
    persistedUpload: PersistedUpload;
    onSuccess: (item: UploadItem, mediaObject: MediaObject) => void;
    onError: (error: any) => void;
  }) => Promise<void>;
}

// Create the context with a default value
export const MediaUploadContext = createContext<MediaUploadContextType | undefined>(undefined);

// Provider component
export const MediaUploadProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const PERSISTED_UPLOADS_KEY = 'beCreatives_persisted_uploads';
  const { toast } = useToast();
  
  // Get selected brand for upload API calls
  const { selectedBrand } = useBrandSelection();
  
  const [isUploading, setIsUploading] = useState(false);
  const [uploadItems, setUploadItems] = useState<UploadItem[]>([]);
  const [persistedUploads, setPersistedUploads] = useState<PersistedUpload[]>([]);
  const isFirstLoad = useRef(true);
  const currentUploads = useRef<Record<string, tus.Upload>>({});

  useEffect(() => {
    initDB().catch((error) => {
      console.error('Failed to initialize IndexedDB for media uploads:', error);
      toast({
        title: 'Warning',
        description:
          'Could not initialize local file cache. File resume might require re-selection.',
        variant: 'info',
      });
    });
  }, [toast]);

  useEffect(() => {
    if (uploadItems.length === 0) {
      setIsUploading(false);
    }
  }, [uploadItems]);

  useEffect(() => {
    const loadPersistedUploads = async () => {
      const persistedData = localStorage.getItem(PERSISTED_UPLOADS_KEY);
      if (persistedData) {
        try {
          setPersistedUploads(JSON.parse(persistedData));
        } catch (error) {
          console.error('Error loading persisted uploads:', error);
        }
      }
      setTimeout(() => {
        isFirstLoad.current = false;
      }, 100);
    };
    loadPersistedUploads();
  }, []);

  useEffect(() => {
    if (!isFirstLoad.current) {
      localStorage.setItem(PERSISTED_UPLOADS_KEY, JSON.stringify(persistedUploads));
    }
  }, [persistedUploads]);

  const getUploadUrl = async (file: File, userPath: string[]) => {
    try {
      const response = await mediaService.getUploadUrl({ 
        file, 
        userPath, 
        brandId: selectedBrand?.id 
      });
      console.debug('🔧 MediaUploadContext.getUploadUrl: Passing brandId to mediaService:', selectedBrand?.id);
      return response;
    } catch (error) {
      console.error('Error getting upload URL:', error);
      return null;
    }
  };

  const getTimeoutStrategy = (fileSizeMB: number, baseTimeout: number) => {
    let dynamicTimeout: number;
    let maxRetries: number;
    let initialInterval: number;
    let maxInterval: number;
    let backoffMultiplier: number;
    
    if (fileSizeMB < 100) {
      // Small files: Quick processing
      dynamicTimeout = Math.min(baseTimeout + (fileSizeMB * 1000), 90000); // +1s per MB, max 90s
      maxRetries = 6;
      initialInterval = 2000; // 2s
      maxInterval = 8000; // 8s
      backoffMultiplier = 1.5;
    } else if (fileSizeMB < 500) {
      // Medium-large files: Moderate patience
      dynamicTimeout = Math.min(baseTimeout + (fileSizeMB * 2000), 180000); // +2s per MB, max 3min
      maxRetries = 8;
      initialInterval = 3000; // 3s
      maxInterval = 12000; // 12s
      backoffMultiplier = 1.4;
    } else if (fileSizeMB < 1000) {
      // Large files (500MB-1GB): High patience
      dynamicTimeout = Math.min(baseTimeout + (fileSizeMB * 3000), 300000); // +3s per MB, max 5min
      maxRetries = 10;
      initialInterval = 5000; // 5s
      maxInterval = 20000; // 20s
      backoffMultiplier = 1.3;
    } else {
      // Very large files (1GB+): Maximum patience
      dynamicTimeout = Math.min(baseTimeout + (fileSizeMB * 4000), 600000); // +4s per MB, max 10min
      maxRetries = 12;
      initialInterval = 8000; // 8s
      maxInterval = 30000; // 30s
      backoffMultiplier = 1.25;
    }

    return {
      dynamicTimeout,
      maxRetries,
      initialInterval,
      maxInterval,
      backoffMultiplier,
    };
  }

  const createUploadFromUploadItem = (
    item: UploadItem,
    resolve: (value: MediaObject) => void,
    reject: (reason?: any) => void,
  ) => {
    const MAX_RETRIES = 5;

    // Store file in IndexedDB if id is present and it's a file object
    if (item.id && item.file instanceof File) {
      storeFile(item.id, item.file).catch((err) => {
        console.error(`Failed to store file ${item.id} for resumable upload in IndexedDB:`, err);
      });
    } else {
      console.warn(
        `Skipped storing file in IndexedDB. ID: ${item.id}, File is File: ${item.file instanceof File}`,
      );
    }

    const upload = new tus.Upload(item.file, {
      uploadUrl: item.uploadUrl,
      headers: {
        'x-signature': item.uploadToken,
        Authorization: mediaService.headers.Authorization,
      },
      retryDelays: [0, 1000, 3000, 5000, 10000],
      chunkSize: 6 * 1024 * 1024,
      addRequestId: true,
      storeFingerprintForResuming: true,
      removeFingerprintOnSuccess: true,
      fingerprint: async (file) => `${item.id}-${file.name}-${file.size}`,
      onShouldRetry(_error, retryAttempt) {
        return retryAttempt < MAX_RETRIES;
      },
      onError: (error) => {
        console.error('Upload failed:', item.fileName, error);
        const errorMessage =
          typeof error === 'string' ? error : (error as Error)?.message || 'Unknown upload error';
        setUploadItems((prevItems) =>
          prevItems.map((uploadItem) =>
            uploadItem.id === item.id
              ? { ...uploadItem, status: 'failed', progress: 0, error: errorMessage }
              : uploadItem,
          ),
        );
        reject(error);
      },
      onProgress: (bytesSent, bytesTotal) => {
        const progress = (bytesSent / bytesTotal) * 100;
        setUploadItems((prevItems) =>
          prevItems.map((uploadItem) =>
            uploadItem.id === item.id
              ? { ...uploadItem, progress, status: 'in-progress' }
              : uploadItem,
          ),
        );
        setPersistedUploads((prev) =>
          prev.map((upload) => (upload.id === item.id ? { ...upload, progress } : upload)),
        );
      },
      onSuccess: async () => {
        setUploadItems((prevItems) =>
          prevItems.map((uploadItem) =>
            uploadItem.id === item.id
              ? { ...uploadItem, status: 'uploaded', progress: 100 }
              : uploadItem,
          ),
        );
        let media: MediaObject | null = null;
        const startTime = Date.now();
        
        // Enhanced timeout calculation for very large files
        const fileSizeMB = item.file.size / (1024 * 1024);
        const baseTimeout = 30000; // 30 seconds base
        
        // Tiered timeout strategy for different file sizes
        const {
          dynamicTimeout,
          maxRetries,
          initialInterval,
          maxInterval,
          backoffMultiplier,
        } = getTimeoutStrategy(fileSizeMB, baseTimeout);

        const verifyWithBackoff = async (attempt: number = 1, interval: number = initialInterval): Promise<MediaObject | null> => {
          const elapsed = Date.now() - startTime;
          
          // Check timeout - generous for large files
          if (elapsed >= dynamicTimeout) {
            return null;
          }
          
          // Check retry limit
          if (attempt > maxRetries) {
            return null;
          }
          
          try {
            // Longer individual request timeout for large files
            const requestTimeoutMs = fileSizeMB > 500 ? 20000 : 15000; // 20s for large files, 15s otherwise
            const controller = new AbortController();
            const requestTimeout = setTimeout(() => controller.abort(), requestTimeoutMs);
            
            const mediaResult = await Promise.race([
              mediaService.getMediaItemById(item.id!, true, Date.now()),
              new Promise<null>((_, reject) => {
                controller.signal.addEventListener('abort', () => reject(new Error('Request timeout')));
              })
            ]).catch((error) => {
              clearTimeout(requestTimeout);
              if (error.message === 'Request timeout') {
                console.warn(`⏱️ Individual request timeout (${requestTimeoutMs / 1000}s) on attempt ${attempt} for ${item.id}`);
              } else {
                console.error(`❌ API error on attempt ${attempt} for ${item.id}:`, error.message);
              }
              return null;
            });
            
            clearTimeout(requestTimeout);
            
            if (mediaResult) {
              return mediaResult;
            }
            
            // Don't wait on the final attempt
            if (attempt >= maxRetries) {
              return null;
            }
            
            // Wait with current interval
            await wait(interval);
            
            // Calculate next interval with backoff
            const nextInterval = Math.min(interval * backoffMultiplier, maxInterval);
            
            // Recursive call with incremented attempt
            return verifyWithBackoff(attempt + 1, nextInterval);
            
          } catch (error) {
            console.error(`❌ Unexpected error on attempt ${attempt} for ${item.id}:`, error);
            
            // For unexpected errors, still retry but log the issue
            if (attempt >= maxRetries) {
              return null;
            }
            
            await wait(interval);
            
            const nextInterval = Math.min(interval * backoffMultiplier, maxInterval);
            return verifyWithBackoff(attempt + 1, nextInterval);
          }
        };
        
        // Start verification process
        media = await verifyWithBackoff();

        if (item.id) {
          try {
            await deleteFile(item.id); // Delete from IndexedDB on success
          } catch (err) {
            console.error(
              `Failed to delete file ${item.id} from IndexedDB after successful upload:`,
              err,
            );
          }
        }

        setUploadItems((prevItems) => prevItems.filter((uploadItem) => uploadItem.id !== item.id));
        delete currentUploads.current[item.id! as string];

        if (!media) {
          const timeoutMessage = `Media item ${item.id} not found after ${Math.round(dynamicTimeout / 1000)} seconds timeout (${fileSizeMB.toFixed(1)}MB file)`;
          Logger.warn(timeoutMessage);
          reject(new Error(timeoutMessage));
          return;
        }
        // Remove from persisted uploads in localStorage as tus handles its own resume logic for active uploads
        // and we handle our persisted list for page reloads.
        setPersistedUploads((prev) => prev.filter((upload) => upload.id !== item.id));
        resolve(media);
      },
    });
    return upload;
  };

  const doUpload = (item: UploadItem) => {
    return new Promise<MediaObject>((resolve, reject) => {
      if (!item.id) {
        // This case should ideally not happen if handleUpload correctly assigns IDs
        console.error('Item ID is missing before starting upload:', item);
        reject(new Error('Item ID is missing.'));
        return;
      }
      // Save to persisted uploads (for our localStorage based persistence)
      setPersistedUploads((prev) =>
        uniqBy(
          [
            ...prev,
            {
              id: item.id!,
              fileName: item.fileName,
              fileSize: item.fileSize,
              progress: item.progress,
              uploadUrl: item.uploadUrl,
              uploadToken: item.uploadToken,
              path: item.path,
              fileType: item.file.type,
            },
          ],
          'id',
        ),
      );

      // createUploadFromUploadItem now handles storing the file in IndexedDB
      const upload = createUploadFromUploadItem(item, resolve, reject);
      currentUploads.current[item.id! as string] = upload;
      upload.start();
    });
  };

  const handleUpload = async (params: {
    files: File[];
    userPath: string[];
    onSuccess: (items: UploadItem[]) => void;
    onError: (error: any) => void;
    onSingleFileError: (item: UploadItem, error: any) => void;
    onSingleFileSuccess: (item: UploadItem, mediaObject: MediaObject) => void;
  }) => {
    const { files, userPath, onSuccess, onError, onSingleFileError, onSingleFileSuccess } = params;
    const storageUsage = await mediaService.getStorageUsageStatus();
    if (!storageUsage) {
      onError(new Error('Storage usage not available'));
      return;
    }

    // Check for duplicate filenames in current upload queue
    const duplicateFiles: string[] = [];
    files.forEach((file) => {
      const isDuplicate = uploadItems.some((existingItem) => 
        existingItem.fileName === file.name && 
        existingItem.status !== 'failed' // Don't consider failed uploads as duplicates
      );
      if (isDuplicate) {
        duplicateFiles.push(file.name);
      }
    });

    // If any duplicates found, show user-friendly error message
    if (duplicateFiles.length > 0) {
      const duplicateList = duplicateFiles.length === 1 
        ? `'${duplicateFiles[0]}'` 
        : duplicateFiles.map(name => `'${name}'`).join(', ');
      
      const errorMessage = duplicateFiles.length === 1
        ? `A file named ${duplicateList} has already been uploaded.`
        : `Files with these names have already been uploaded: ${duplicateList}.`;
      
      onError(new Error(errorMessage));
      return;
    }

    const availableSpace = storageUsage.max_storage - storageUsage.used_storage;
    const totalUploadSize = files.reduce((total, file) => total + file.size, 0);
    if (totalUploadSize > availableSpace) {
      onError(new Error('Not enough storage space'));
      return;
    }

    const newUploadItems = files.map<UploadItem>((file) => ({
      id: undefined, // ID will be set after getting upload URL
      tempId: uuidv4(),
      fileName: file.name,
      fileSize: file.size,
      file,
      progress: 0,
      status: 'pending',
      uploadUrl: '',
      uploadToken: '',
      path: userPath,
    }));
    setUploadItems((prev) => [...prev, ...newUploadItems]);
    if (files.length > 0) setIsUploading(true);

    const itemsWithUrlPromises = newUploadItems.map(async (item) => {
      const urlDetails = await getUploadUrl(item.file, item.path);
      if (!urlDetails || !urlDetails.id || !urlDetails.upload_url || !urlDetails.upload_token) {
        toast({
          title: 'Failed to prepare upload',
          description: `Could not get upload details for: ${item.fileName}`,
          variant: 'destructive',
        });
        onSingleFileError(item, new Error('Failed to get upload URL/ID'));
        return null; // Indicate failure for this item
      }
      return {
        ...item,
        id: urlDetails.id,
        uploadUrl: urlDetails.upload_url,
        uploadToken: urlDetails.upload_token,
        status: 'pending' as const, // Explicitly set status
      };
    });

    const processedItems = (await Promise.all(itemsWithUrlPromises)).filter(
      Boolean,
    ) as UploadItem[];

    // Update uploadItems state with new IDs and URLs
    setUploadItems((prev) =>
      prev
        .map((ui) => {
          const processed = processedItems.find((p) => p.tempId === ui.tempId);
          return processed ? { ...ui, ...processed, status: 'pending' as const } : ui;
        })
        .filter((ui) => ui.status !== 'failed'),
    ); // Remove items that failed to get URL

    if (!processedItems.length && files.length > 0) {
      onError(new Error('Failed to prepare any files for upload.'));
      setIsUploading(false);
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, 100)); // Allow state to settle

    const uploadPromises = processedItems.map((item) =>
      doUpload(item)
        .then((mediaObject) => onSingleFileSuccess(item, mediaObject))
        .catch((error) => onSingleFileError(item, error)),
    );

    Promise.all(uploadPromises)
      .then(() => {
        onSuccess(processedItems); // Call overall success if all individual uploads handled (even if some failed)
      })
      .catch((error) => {
        // This catch is for errors in Promise.all setup, individual errors handled above
        onError(error);
      })
      .finally(() => {
        // Check if any uploads are still 'in-progress' or 'pending'
        const stillUploading = uploadItems.some(
          (item) => item.status === 'in-progress' || item.status === 'pending',
        );
        if (!stillUploading) {
          setIsUploading(false);
        }
      });
  };

  const uploadCanBeCancelled = (id: string) => {
    const item = uploadItems.find((item) => item.id === id);
    return !!item && item.status === 'in-progress' && !!currentUploads.current[id];
  };

  const stopUpload = async (id: string) => {
    const upload = currentUploads.current[id];
    const item = uploadItems.find((i) => i.id === id);

    if (item && upload) {
      upload.abort();
      delete currentUploads.current[id];
    }

    // Call the cancel API endpoint to notify the server
    // Commented out as it is no longer used
    // if (id) {
    //   try {
    //     await Api.media.cancelUpload(id);
    //     console.log(`Successfully cancelled upload ${id} on server`);
    //   } catch (error) {
    //     console.error(`Failed to cancel upload ${id} on server:`, error);
    //     // Continue with local cleanup even if API call fails
    //     toast({
    //       title: 'Warning',
    //       description: 'Upload cancelled locally, but server notification failed.',
    //       variant: 'destructive',
    //     });
    //   }
    // }

    setUploadItems((prev) => prev.filter((i) => i.id !== id));
    setPersistedUploads((prev) => prev.filter((i) => i.id !== id));
    if (id) {
      try {
        await deleteFile(id);
      } catch (err) {
        console.error(`Failed to delete file ${id} from IndexedDB on removal:`, err);
      }
    }
  };

  const removePersistedUpload = async (id: string) => {
    setPersistedUploads((prev) => prev.filter((item) => item.id !== id));
    if (id) {
      try {
        await deleteFile(id);
      } catch (err) {
        console.error(
          `Failed to delete file ${id} from IndexedDB on removal of persisted upload:`,
          err,
        );
      }
    }
  };

  const validResumableUploads = () => {
    return uniqBy(
      persistedUploads.filter((item) => {
        const uploadItem = uploadItems.find((upload) => upload.id === item.id);
        return !uploadItem || uploadItem.status === 'failed';
      }),
      'id',
    );
  };

  const resumeUploadWithFile = async (params: {
    file: File;
    persistedUpload: PersistedUpload;
    onSuccess: (item: UploadItem, mediaObject: MediaObject) => void;
    onError: (error: any) => void;
  }) => {
    const { file, persistedUpload, onSuccess, onError } = params;

    const newFileSignature = `${file.name}-${file.size}`;
    const prevFileSignature = `${persistedUpload.fileName}-${persistedUpload.fileSize}`;
    if (newFileSignature !== prevFileSignature) {
      onError(new Error('File signature does not match. Please select the correct file.'));
      return;
    }

    const uploadItem: UploadItem = {
      id: persistedUpload.id,
      tempId: uuidv4(),
      fileName: file.name,
      fileSize: file.size,
      file: file,
      progress: persistedUpload.progress, // Use persisted progress
      status: 'pending', // Will change to in-progress once tus starts
      uploadUrl: persistedUpload.uploadUrl,
      uploadToken: persistedUpload.uploadToken,
      path: persistedUpload.path,
      error: undefined,
    };

    setUploadItems((prev) => uniqBy([...prev, uploadItem], 'id'));
    setIsUploading(true);

    // The createUploadFromUploadItem function will handle storing the file in IndexedDB again if needed,
    // though it should already be there. It also handles creating the tus.Upload instance.
    const upload = createUploadFromUploadItem(
      uploadItem,
      (mediaObject) => onSuccess(uploadItem, mediaObject),
      (error) => onError(error),
    );

    currentUploads.current[uploadItem.id!] = upload;

    // Attempt to resume with tus-js-client's built-in resume
    const previousTusUploads = await upload.findPreviousUploads();
    if (previousTusUploads.length > 0) {
      upload.resumeFromPreviousUpload(previousTusUploads[0]);
    }
    // If not resumable by tus (e.g. fingerprint changed or not found), it will start fresh.
    upload.start();
  };

  const resumeUpload = async (params: {
    persistedUpload: PersistedUpload;
    onSuccess: (item: UploadItem, mediaObject: MediaObject) => void;
    onError: (error: any) => void;
  }) => {
    const { persistedUpload, onSuccess, onError } = params;
    if (!persistedUpload || !persistedUpload.id) {
      onError(new Error('Invalid persisted upload object provided.'));
      return;
    }

    try {
      const fileFromCache = await getFile(persistedUpload.id);
      if (fileFromCache) {
        await resumeUploadWithFile({
          file: fileFromCache,
          persistedUpload,
          onSuccess,
          onError,
        });
      } else {
        const error = new Error('File not found in local cache. Please select the file again.');
        error.name = 'FILE_NOT_IN_CACHE'; // For UI to identify this specific error
        onError(error);
      }
    } catch (error) {
      console.error(
        `Error during getFile in resumeUpload (IndexedDB check). ID: ${persistedUpload.id}:`,
        error,
      );
      onError(error);
    }
  };

  const value: MediaUploadContextType = {
    uploadItems,
    persistedUploads,
    isUploading,
    handleUpload,
    stopUpload,
    removePersistedUpload,
    currentUploads: currentUploads.current,
    validResumableUploads,
    uploadCanBeCancelled,
    resumeUpload,
    resumeUploadWithFile,
  };

  return <MediaUploadContext.Provider value={value}>{children}</MediaUploadContext.Provider>;
};

export const useMediaUpload = (): MediaUploadContextType => {
  const context = useContext(MediaUploadContext);
  if (context === undefined) {
    throw new Error('useMediaUpload must be used within a MediaUploadProvider');
  }
  return context;
};
